import { Component, OnInit } from "@angular/core";
import {
  BewerkingDto,
  BestellingBewerkingDto,
  BestellingNeutDto,
} from "../shared/api-models/models";
import { BackendService } from "../core/services/backend.service";
import { UiStateService } from "../core/services/ui-state-service";
import { filter, switchMap, map, takeUntil } from "rxjs/operators";
import { BaseComponent } from "../shared/base.component";
import { BewerkingUtils } from "../shared/api-models/bewerkingDto";
import { GraphicsService } from '../core/services/graphics.service';
import { DorpelUtils } from "../shared/models/dorpel-type.model";

type BewerkingError = {
  depth: string | null,
  overflow: string | null
}

const MAX_BEWERKING_DEPTH = 20;

@Component({
  selector: "app-bewerkingen-horiz",
  templateUrl: "./bewerkingen-horiz.component.html",
  styleUrls: ["./bewerkingen-horiz.component.css"],
})
export class BewerkingenHorizComponent extends BaseComponent implements OnInit {
  public bewerkingen: BewerkingDto[] = [];
  public selectedBwCodes: BestellingBewerkingDto[] = [];
  public selectedBwErrors: BewerkingError[] = [];
  public selectorDisabled: boolean = true;
  public errorMessage: string | null = null;
  public neutLengte: number = 0;

  constructor(
    private backendService: BackendService,
    private uiState: UiStateService,
    private graphicsService: GraphicsService
  ) {
    super();
  }

  ngOnInit() {
    this.uiState.dorpelCode$
      .pipe(
        filter((dorpelCode) => !!dorpelCode),
        switchMap((dorpelCode) =>
          this.backendService
            .getAllBewerkingen()
            .pipe(
              map((bws) => bws.filter((bw) => bw.dorpelCode === dorpelCode))
            )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((bewerkingen) => {
        this.selectorDisabled = this.isSlidingDorpel() && !this.uiState.currentDorpel.schema;
        this.bewerkingen = bewerkingen;

        this.selectedBwCodes = this.uiState.currentDorpel.bewerkingen;

        if (this.selectedBwCodes.length) {
          this.uiState.setBewerkingenOptions(this.selectedBwCodes);
        }
      });

    this.uiState.schema$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((schema) => {
        this.setDisabledStateBySchema(schema);
      });

    this.uiState.neuten$
      .pipe(filter((neuten) => !!neuten))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((neuten) => {
        if (neuten[0]) {
          this.updateNeutHeight(neuten[0]);
        }
      })
  }

  private updateNeutHeight(neut: BestellingNeutDto): void {
    let neutDto = this.uiState.getNeutDtoByCodeAndPositie(
      neut.neutCode,
      neut.positie
    );

    if (neutDto && neutDto.neutlengte) {
      this.neutLengte = parseInt(neutDto.neutlengte);
    }
  }

  public onBewerkingSelected(e: any) {
    this.errorMessage = null;

    if (e.target.value) {
      let bewerking: BestellingBewerkingDto = this.getDataForCode(e.target.value);

      //VALIDATION FOR A CERTAIN EDGE TO NEUT DISTANCE FOR VERSTEK:

      // let verstekError = this.validateVersteksponning(bewerking.description);

      // if (verstekError) {
      //   this.errorMessage = verstekError;
      //   return;
      // }

      let duplicateBewerkingError = this.preventDuplicateValues(bewerking.description);
      if (duplicateBewerkingError) {
        this.errorMessage = duplicateBewerkingError;
        return;
      }

      this.cleanupBewerkingConflicts(bewerking.description);
      this.revalidateBwerkingenOptions();
      this.selectedBwCodes.push(bewerking);
      this.uiState.setBewerkingenOptions(this.selectedBwCodes);
      this.uiState.triggerRedraw();
      e.target.value = "";
    }
  }

  public showKloosterConfiguration(bewerkingDescription: string): boolean {
    return BewerkingUtils.isKloostersponning(bewerkingDescription)
  }

  public showRenovatieConfiguration(bewerkingDescription: string): boolean {
    return BewerkingUtils.isRenovatiesponning(bewerkingDescription)
  }


  private preventDuplicateValues(bewerkingDescription: string): string | null {
    const DUPLICATE_ERROR_MESSAGE = "U heeft al een klooster/renovatie sponning voor deze zijde ingegeven. verwijder deze eerst voor u een nieuwe aanmaakt."
    let addingKloostersponnigLeft = BewerkingUtils.isKloostersponningLeft(bewerkingDescription);
    let addingKloostersponnigRight = BewerkingUtils.isKloostersponningRight(bewerkingDescription);
    let addingRenovatiesponnigLeft = BewerkingUtils.isRenovatiesponningLeft(bewerkingDescription);
    let addingRenovatiesponnigRight = BewerkingUtils.isRenovatiesponningRight(bewerkingDescription);

    if (addingKloostersponnigLeft || addingKloostersponnigRight) {
      let hasKloostersponningBoth = this.selectedBwCodes.find(bw => BewerkingUtils.isKloostersponningLeftAndRight(bw.description));
      return hasKloostersponningBoth ? DUPLICATE_ERROR_MESSAGE : null;
    } else if (addingRenovatiesponnigLeft || addingRenovatiesponnigRight) {
      let hasRenovatiesponningBoth = this.selectedBwCodes.find(bw => BewerkingUtils.isRenovatiesponningLeftAndRight(bw.description));
      return hasRenovatiesponningBoth ? DUPLICATE_ERROR_MESSAGE : null;
    }
  }

  //VALIDATION FOR A CERTAIN EDGE TO NEUT DISTANCE FOR VERSTEK:
  // private validateVersteksponning(bewerkingDescription: string): string | null {
  //   if (BewerkingUtils.isVersteksponningLeft(bewerkingDescription)) {
  //     let maatA = this.uiState.currentDorpel.maatA;
  //     let leftVerstek = this.getVerstekSponningData().angle;
  //     let minimumLeftNecessarySpace = this.graphicsService.calculateVerstekLengthByAngle(leftVerstek) //make side specific
  //     if (!maatA || maatA < minimumLeftNecessarySpace) {
  //       return `maatA must be at least ${minimumLeftNecessarySpace} to add a verstek left`;
  //     }
  //   } else if (BewerkingUtils.isVersteksponningRight(bewerkingDescription)) {
  //     let maatZ = this.uiState.currentDorpel.maatZ;
  //     let rightVerstek = this.getVerstekSponningData().angle;
  //     let minimumRightNecessarySpace = this.graphicsService.calculateVerstekLengthByAngle(rightVerstek) //make side specific

  //     if (!maatZ || maatZ < minimumRightNecessarySpace) {
  //       return `maatZ must be at least ${minimumRightNecessarySpace} to add a verstek right`;
  //     }
  //   }

  //   return null;
  // }

  public shouldShowCustomMeasurements(bewerkingDescription: string): boolean {
    return !(BewerkingUtils.isVersteksponningLeft(bewerkingDescription) || BewerkingUtils.isVersteksponningRight(bewerkingDescription) || BewerkingUtils.isLosEindstuk(bewerkingDescription) || BewerkingUtils.isVullat(bewerkingDescription));
  }

  public shouldShowVerstekOption(bewerkingDescription: string): boolean {
    return BewerkingUtils.isVersteksponningLeft(bewerkingDescription) || BewerkingUtils.isVersteksponningRight(bewerkingDescription);
  }

  public shouldShowVerstekNeutCutOption(bewerkingDescription: string, index: number): boolean {
    return BewerkingUtils.isVersteksponningLeft(bewerkingDescription) || BewerkingUtils.isVersteksponningRight(bewerkingDescription); //customer requested to always show this option

    // let dorpelHeight = DorpelUtils.getThicknessFromDorpelCode(this.uiState.currentDorpel.dorpelCode)

    // if (BewerkingUtils.isVersteksponningLeft(bewerkingDescription)) {
    //   let shouldShow = (this.uiState.currentDorpel.maatA || 0) < dorpelHeight;
    //   if (!shouldShow) {
    //     this.selectedBwCodes[index].cutNeut = null;
    //   } else if (!this.selectedBwCodes[index].cutNeut) {
    //     this.selectedBwCodes[index].cutNeut = false;
    //   }
    //   return shouldShow;
    // }

    // if (BewerkingUtils.isVersteksponningRight(bewerkingDescription)) {
    //   let shouldShow = (this.uiState.currentDorpel.maatZ || 0) < dorpelHeight;
    //   if (!shouldShow) {
    //     this.selectedBwCodes[index].cutNeut = null;
    //   } else if (!this.selectedBwCodes[index].cutNeut) {
    //     this.selectedBwCodes[index].cutNeut = false;
    //   }
    //   return shouldShow;
    // }

    // return false;
  }

  private cleanupBewerkingConflicts(newBewerkingDecription: string): void {
    let addingKloostersponnigLeft = BewerkingUtils.isKloostersponningLeft(newBewerkingDecription);
    let addingKloostersponnigRight = BewerkingUtils.isKloostersponningRight(newBewerkingDecription);
    let addingKloostersponnigBoth = BewerkingUtils.isKloostersponningLeftAndRight(newBewerkingDecription);

    let addingRenovatiesponnigLeft = BewerkingUtils.isRenovatiesponningLeft(newBewerkingDecription);
    let addingRenovatiesponnigRight = BewerkingUtils.isRenovatiesponningRight(newBewerkingDecription);
    let addingRenovatiesponnigBoth = BewerkingUtils.isRenovatiesponningLeftAndRight(newBewerkingDecription);

    let addingVersteksponnigLeft = BewerkingUtils.isVersteksponningLeft(newBewerkingDecription);
    let addingVersteksponnigRight = BewerkingUtils.isVersteksponningRight(newBewerkingDecription);

    let addingLosEindstuck = BewerkingUtils.isLosEindstuk(newBewerkingDecription);
    let addingVullat = BewerkingUtils.isVullat(newBewerkingDecription);

   
    if (addingKloostersponnigBoth || addingRenovatiesponnigBoth) {
      this.selectedBwCodes = [];
    } else if (addingKloostersponnigLeft) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isRenovatiesponningLeft(bw.description) && !BewerkingUtils.isRenovatiesponningLeftAndRight(bw.description) && !BewerkingUtils.isKloostersponningLeft(bw.description) && !BewerkingUtils.isVersteksponningLeft(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingKloostersponnigRight) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isRenovatiesponningRight(bw.description) && !BewerkingUtils.isRenovatiesponningLeftAndRight(bw.description) && !BewerkingUtils.isKloostersponningRight(bw.description) && !BewerkingUtils.isVersteksponningRight(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingRenovatiesponnigLeft) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isKloostersponningLeft(bw.description) && !BewerkingUtils.isKloostersponningLeftAndRight(bw.description) && !BewerkingUtils.isRenovatiesponningLeft(bw.description) && !BewerkingUtils.isVersteksponningLeft(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingRenovatiesponnigRight) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isKloostersponningRight(bw.description) && !BewerkingUtils.isKloostersponningLeftAndRight(bw.description) && !BewerkingUtils.isRenovatiesponningRight(bw.description) && !BewerkingUtils.isVersteksponningRight(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingVersteksponnigLeft) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isKloostersponningLeft(bw.description) && !BewerkingUtils.isRenovatiesponningLeft(bw.description) && !BewerkingUtils.isKloostersponningLeftAndRight(bw.description) && !BewerkingUtils.isRenovatiesponningLeftAndRight(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingVersteksponnigRight) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isKloostersponningRight(bw.description) && !BewerkingUtils.isRenovatiesponningRight(bw.description) && !BewerkingUtils.isKloostersponningLeftAndRight(bw.description) && !BewerkingUtils.isRenovatiesponningLeftAndRight(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingLosEindstuck) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isLosEindstuk(bw.description));
      this.selectedBwCodes = cleansedList;
    } else if (addingVullat) {
      let cleansedList = this.selectedBwCodes.filter(bw => !BewerkingUtils.isVullat(bw.description));
      this.selectedBwCodes = cleansedList;
    }
  }

  private setDisabledStateBySchema(schema?: string) {
    let hasSchema = !!schema;
    this.selectorDisabled = !hasSchema;
  }

  private revalidateBwerkingenOptions(): boolean {
    let isValid = true;
    this.selectedBwErrors = [];
    for (let i = 0; i < this.selectedBwCodes.length; i++) {
      let bw = this.selectedBwCodes[i];
      let errorObject: BewerkingError = {
        depth: null,
        overflow: null
      }

      if (!BewerkingUtils.isKloostersponning(bw.description)) {
        this.selectedBwErrors.push(errorObject);
        continue; //we only validate for klostersponning;
      }

      if (bw.customDepth > MAX_BEWERKING_DEPTH) {
        errorObject.depth = "Voor deze neut ondersteunen wij diepte tot 20mm";
        isValid = false;
      }

      if (this.neutLengte - bw.customLength - bw.customOffset < 0) {
        errorObject.overflow = "Uw maten zijn gecombineerdbreder dan de neut";
        isValid = false;
      }

      this.selectedBwErrors.push(errorObject);
    }

    return isValid;
  }

  private isSlidingDorpel() {
    const dorpelCode = this.uiState.currentDorpel.dorpelCode;
    const chosenDorpel = this.uiState.dorpelChoices.find(d => d.dorpelCode === dorpelCode);
    let isCurrentDoorSliding = chosenDorpel.dorpelsoort === "Hefschuifdorpel";

    return isCurrentDoorSliding;
  }

  public onBewerkingRemoved(index: number): void {
    this.errorMessage = null;
    this.selectedBwCodes.splice(index, 1);
    this.revalidateBwerkingenOptions();
    this.uiState.setBewerkingenOptions(this.selectedBwCodes);
    this.uiState.triggerRedraw();
  }

  public onBewerkingCustomDepthChanged(e: any, index: number): void {
    this.selectedBwCodes[index].customDepth = e.target.value ? parseInt(e.target.value) : null;
    let isValid = this.revalidateBwerkingenOptions();
    if (!isValid) return;
    this.uiState.setBewerkingenOptions(this.selectedBwCodes);
    this.uiState.triggerRedraw();
  }

  public onBewerkingCustomOffsetChanged(e: any, index: number): void {
    this.selectedBwCodes[index].customOffset = e.target.value ? parseInt(e.target.value) : null;
    let isValid = this.revalidateBwerkingenOptions();
    if (!isValid) return;
    this.uiState.setBewerkingenOptions(this.selectedBwCodes);
    this.uiState.triggerRedraw();
  }

  public onVerstekOrentatieChange(e: any, index: number): void {
    this.selectedBwCodes[index].orientatie = e.target.value;
    this.uiState.setBewerkingenOptions(this.selectedBwCodes);
    this.uiState.triggerRedraw();
  }

  public onCutNeutChanged(e: any, index: number): void {
    this.uiState.setBewerkingenOptions(this.selectedBwCodes);
  }

  public onBewerkingCustomLengthChanged(e: any, index: number): void {
    this.selectedBwCodes[index].customLength = e.target.value ? parseInt(e.target.value) : null;
    let isValid = this.revalidateBwerkingenOptions();
    if (!isValid) return;
    this.uiState.setBewerkingenOptions(this.selectedBwCodes);
    this.uiState.triggerRedraw();
  }

  public getData(): BestellingBewerkingDto[] {
    return this.selectedBwCodes;
  }

  public getDataForCode(code: string): BestellingBewerkingDto {
    let description = this.bewerkingen.find((b) => b.bewerkingCode === code).omschrijving;
    let defaults = BewerkingUtils.getDefaults(description)
    let bewerkingData: BestellingBewerkingDto = {
      bewerkingCode: code,
      description: description,
      customDepth: defaults?.depth,
      customLength: defaults?.length,
      customOffset: defaults?.offset
    }

    if (defaults?.orientatie) {
      bewerkingData.orientatie = defaults.orientatie
    }

    if (defaults?.cutNeut) {
      bewerkingData.cutNeut = defaults.cutNeut
    }

    return bewerkingData;
  }

  private getVerstekSponningData() { //TODO will read dynamic data from view (field)
    return {
      angle: 45
    }
  }
}
